import { type ReactNode, type RefObject, useRef } from "react";
import { gsap, useGSAP } from "../lib/gsap";
import { stickySectionExpansionInset } from "../lib/sticky-header";
import type { SmoothScrollController } from "./SmoothScroll";

interface CuratedSectionPanelProps {
  children: ReactNode;
  id: string;
  open: boolean;
  onMotionComplete: () => void;
  scrollElementRef: RefObject<HTMLElement | null>;
  scrollControllerRef: RefObject<SmoothScrollController | null>;
  expandFromTopRef: RefObject<number | null>;
}

interface CollapseAnchor {
  currentScrollTop: number;
  targetScrollTop: number;
}

function getCollapseAnchor(
  panelElement: HTMLElement,
  scrollElement: HTMLElement | null
): CollapseAnchor | null {
  const sectionElement = panelElement.closest<HTMLElement>(".curated-section");
  const toggleElement = sectionElement?.querySelector<HTMLElement>(
    ".curated-section__toggle"
  );
  if (!sectionElement || !toggleElement || !scrollElement) return null;

  const scrollTop = scrollElement.scrollTop;
  const scrollViewportTop =
    scrollElement.getBoundingClientRect().top + scrollElement.clientTop;
  const sectionStart =
    scrollTop + sectionElement.getBoundingClientRect().top - scrollViewportTop;
  const stickyEnd =
    sectionStart + sectionElement.offsetHeight - toggleElement.offsetHeight;

  if (scrollTop <= sectionStart + 0.5 || scrollTop >= stickyEnd - 0.5) {
    return null;
  }

  return {
    currentScrollTop: scrollTop,
    targetScrollTop: Math.max(0, sectionStart)
  };
}

export function CuratedSectionPanel({
  children,
  id,
  open,
  onMotionComplete,
  scrollElementRef,
  scrollControllerRef,
  expandFromTopRef
}: CuratedSectionPanelProps) {
  const panel = useRef<HTMLDivElement>(null);
  const inner = useRef<HTMLDivElement>(null);
  const timeline = useRef<gsap.core.Timeline | null>(null);
  const previousOpen = useRef(open);

  useGSAP(
    () => {
      const panelElement = panel.current;
      const innerElement = inner.current;
      if (!panelElement || !innerElement) return;

      const sectionElement = panelElement.closest<HTMLElement>(
        ".curated-section"
      );
      const toggleElement = sectionElement?.querySelector<HTMLElement>(
        ".curated-section__toggle"
      );

      const cards = gsap.utils.toArray<HTMLElement>(
        ".model-card-shell",
        panelElement
      );
      const reducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      ).matches;
      const isInitialState = previousOpen.current === open;
      const expandFromTop =
        open && !isInitialState ? expandFromTopRef.current : null;
      expandFromTopRef.current = null;
      const scrollElement = scrollElementRef.current;
      const stickyTop = scrollElement
        ? scrollElement.getBoundingClientRect().top + scrollElement.clientTop
        : null;
      const recoverPinnedExpansion =
        toggleElement !== undefined &&
        toggleElement !== null &&
        expandFromTop !== null &&
        stickyTop !== null &&
        expandFromTop < stickyTop - 0.5;
      const expansionInset = recoverPinnedExpansion
        ? stickySectionExpansionInset(expandFromTop, stickyTop)
        : 0;
      const collapseAnchor =
        !open && !isInitialState
          ? getCollapseAnchor(panelElement, scrollElementRef.current)
          : null;
      const setScrollTop = (top: number) => {
        const controller = scrollControllerRef.current;
        if (controller) {
          controller.scrollToPosition(top, true);
        } else if (scrollElementRef.current) {
          scrollElementRef.current.scrollTop = top;
        }
      };
      const wasAnimating = timeline.current?.isActive() ?? false;
      previousOpen.current = open;

      timeline.current?.kill();
      gsap.killTweensOf([
        panelElement,
        innerElement,
        ...(toggleElement ? [toggleElement] : []),
        ...cards
      ]);
      if (toggleElement) gsap.set(toggleElement, { clearProps: "transform" });

      if (isInitialState || reducedMotion) {
        if (collapseAnchor) setScrollTop(collapseAnchor.targetScrollTop);
        gsap.set(panelElement, {
          autoAlpha: open ? 1 : 0,
          height: open ? "auto" : 0,
          overflow: open ? "visible" : "hidden",
          visibility: open ? "visible" : "hidden",
          paddingTop: open ? expansionInset : 0
        });
        gsap.set(innerElement, { clearProps: "transform" });
        gsap.set(cards, { clearProps: "opacity,visibility,transform" });
        if (!isInitialState) onMotionComplete();
        return;
      }

      const currentHeight = panelElement.getBoundingClientRect().height;
      const targetHeight = innerElement.scrollHeight;

      if (open) {
        gsap.set(panelElement, {
          height: currentHeight,
          overflow: "hidden",
          visibility: "visible",
          paddingTop: expansionInset
        });
        if (!wasAnimating && currentHeight <= 1) {
          gsap.set(innerElement, { y: -8 });
          gsap.set(cards, { autoAlpha: 0, y: 10 });
        }

        timeline.current = gsap
          .timeline({
            onComplete: () => {
              gsap.set(panelElement, { height: "auto", overflow: "visible" });
              gsap.set(innerElement, { clearProps: "transform" });
              if (toggleElement) {
                gsap.set(toggleElement, { clearProps: "transform" });
              }
              gsap.set(cards, {
                clearProps: "opacity,visibility,transform"
              });
              onMotionComplete();
            }
          })
          .to(
            panelElement,
            {
              autoAlpha: 1,
              duration: 0.42,
              ease: "power3.out",
              height: targetHeight
            },
            0
          )
          .to(
            innerElement,
            { duration: 0.36, ease: "power3.out", y: 0 },
            0.02
          )
          .to(
            cards,
            {
              autoAlpha: 1,
              duration: 0.3,
              ease: "power2.out",
              stagger: { amount: 0.16, from: "start" },
              y: 0
            },
            0.08
          );

        if (
          recoverPinnedExpansion &&
          toggleElement &&
          expandFromTop !== null &&
          stickyTop !== null
        ) {
          let appliedY = 0;
          const visiblePosition = { top: expandFromTop };
          const setToggleY = gsap.quickSetter(toggleElement, "y", "px");
          timeline.current.to(
            visiblePosition,
            {
              top: stickyTop,
              duration: 0.36,
              ease: "power3.out",
              onUpdate: () => {
                const renderedTop = toggleElement.getBoundingClientRect().top;
                const nativeTop = renderedTop - appliedY;
                appliedY = visiblePosition.top - nativeTop;
                setToggleY(appliedY);
              }
            },
            0
          );
        }
        return;
      }

      gsap.set(panelElement, { height: currentHeight, overflow: "hidden" });
      const collapseTimeline = gsap.timeline({
        onComplete: () => {
          if (collapseAnchor) setScrollTop(collapseAnchor.targetScrollTop);
          gsap.set(panelElement, { visibility: "hidden", paddingTop: 0 });
          onMotionComplete();
        }
      });
      timeline.current = collapseTimeline
        .to(
          cards,
          {
            autoAlpha: 0,
            duration: 0.16,
            ease: "power1.in",
            stagger: { amount: 0.08, from: "end" },
            y: -5
          },
          0
        )
        .to(
          innerElement,
          { duration: 0.24, ease: "power2.in", y: -6 },
          0
        )
        .to(
          panelElement,
          {
            autoAlpha: 0,
            duration: 0.3,
            ease: "power2.inOut",
            height: 0
          },
          0
        );

      if (collapseAnchor) {
        const scrollPosition = { top: collapseAnchor.currentScrollTop };
        collapseTimeline.to(
          scrollPosition,
          {
            top: collapseAnchor.targetScrollTop,
            duration: 0.3,
            ease: "power2.inOut",
            onUpdate: () => setScrollTop(scrollPosition.top)
          },
          0
        );
      }
    },
    {
      dependencies: [
        open,
        onMotionComplete,
        scrollControllerRef,
        scrollElementRef,
        expandFromTopRef
      ],
      scope: panel
    }
  );

  return (
    <div
      ref={panel}
      className="curated-section__body"
      id={id}
      aria-hidden={!open}
      inert={!open}
    >
      <div ref={inner} className="curated-section__inner">
        {children}
      </div>
    </div>
  );
}
